import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Create axios instance with dynamic auth header
  const axiosInstance = axios.create({
    baseURL: API,
    timeout: 30000,
  });

  // Add auth header to requests
  axiosInstance.interceptors.request.use((config) => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      config.headers.Authorization = `Bearer ${storedToken}`;
    }
    return config;
  });

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setSelectedOrg(null);
  }, []);

  // Handle 401 responses
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axiosInstance.get('/auth/me');
      setUser(response.data);
      // For sys_admin, track selected org
      if (response.data.role === 'sys_admin' && response.data.selected_org_id) {
        try {
          const orgResponse = await axiosInstance.get(`/organizations/${response.data.selected_org_id}`);
          setSelectedOrg(orgResponse.data);
        } catch (e) {
          console.error('Failed to fetch selected org:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axiosInstance.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    // Save token to localStorage FIRST - this is critical for page reload
    localStorage.setItem('token', access_token);
    
    // Then update React state
    setToken(access_token);
    setUser(userData);
    
    // Return user data - the calling code will handle navigation
    return userData;
  };

  // SSO login - directly sets token and user from SSO callback
  const ssoLogin = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const register = async (userData) => {
    const response = await axiosInstance.post('/auth/register', userData);
    const { access_token, user: newUser } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    return newUser;
  };

  // For sys_admin to select an organization
  const selectOrganization = async (orgId) => {
    const response = await axiosInstance.post(`/organizations/select/${orgId}`);
    const { access_token, selected_org_id, selected_org_name } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(prev => ({ ...prev, selected_org_id }));
    setSelectedOrg({ org_id: selected_org_id, org_name: selected_org_name });
    return response.data;
  };

  // For sys_admin to deselect organization
  const deselectOrganization = async () => {
    const response = await axiosInstance.post('/organizations/deselect');
    const { access_token } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(prev => ({ ...prev, selected_org_id: null }));
    setSelectedOrg(null);
    return response.data;
  };

  // Check if sys_admin needs to select an org
  const isSysAdmin = user?.role === 'sys_admin';
  const needsOrgSelection = isSysAdmin && !user?.selected_org_id;
  
  // Computed authentication state
  const isAuthenticated = !!user && !!token;

  const value = {
    user,
    token,
    loading,
    login,
    ssoLogin,
    register,
    logout,
    api: axiosInstance,
    // Authentication state
    isAuthenticated,
    // sys_admin org context
    selectedOrg,
    selectOrganization,
    deselectOrganization,
    isSysAdmin,
    needsOrgSelection,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
